const BASE = "/api/orchestrator/usage";

/**
 * One submitting account and what it brought in.
 *
 * `user_id` is null on exactly one row: the documents with no recorded
 * submitter (an .env account, or submitted before submitters were recorded).
 * It is returned rather than dropped so the counts add up to the real total.
 */
export interface SubmitterRow {
  user_id: string | null;
  username: string | null;
  email: string | null;
  full_name: string | null;
  role: string | null;
  /** Set when the account holds an API credential. */
  client_id: string | null;
  is_active: boolean | null;
  documents: number;
  /** Of `documents`, how many arrived through the API rather than the console. */
  via_api: number;
  completed: number;
  failed: number;
  in_progress: number;
  cost_usd: number | null;
  /** False when some documents have no usage recorded: cost is then a floor. */
  cost_complete: boolean | null;
  first_submitted_at: string | null;
  last_submitted_at: string | null;
}

export interface SubmittersResponse {
  status: string;
  /** Null when the request was all-time. */
  from: string | null;
  to: string | null;
  submitters: SubmitterRow[];
  total_documents: number;
}

/** All time unless a window is given; `to` is inclusive. */
export function submittersPath(from?: string, to?: string): string {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const s = qs.toString();
  return `${BASE}/submitters${s ? `?${s}` : ""}`;
}

/** API traffic for one client credential, from the best-effort request log. */
export interface ClientUsageRow {
  client_id: string;
  username: string;
  user_id: string | null;
  requests: number;
  errors: number;
  failed_token_exchanges: number;
  last_request_at: string | null;
  documents_submitted: number;
  estimated_cost_usd: number | null;
}

export interface ClientsUsageResponse {
  status: string;
  from: string;
  to: string;
  clients: ClientUsageRow[];
}

export const clientsUsagePath = (from: string, to: string) =>
  `${BASE}/clients?${new URLSearchParams({ from, to }).toString()}`;
